import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SectionHeading, PageHeader } from "@/components/public/ui";

const TIMELINE = [
  { year: "1986", title: "It Starts on the Pagan River", text: "Smithfield Station opens, and the Pack family plants deep roots in waterfront hospitality." },
  { year: "Early 2000s", title: "The Next Generation", text: "Randy and Brian Pack return home to help lead and grow the family business." },
  { year: "2018", title: "A Group Takes Shape", text: "Pack Brothers Hospitality is formally established alongside the addition of Surry Seafood Co." },
  { year: "2019+", title: "A Vision at Fort Monroe", text: "The company looks toward historic Fort Monroe and begins shaping the vision for 37 North." },
  { year: "2024", title: "A Growing Collection", text: "Vintage Tavern, Decoys Seafood and River Stone Chophouse join the portfolio." },
];

const PRINCIPLES = [
  { t: "Great Food", d: "Seasonal, honest cooking that lets Coastal Virginia's bounty shine." },
  { t: "Genuine Service", d: "Warm, unhurried hospitality that makes everyone feel at home." },
  { t: "Memorable Places", d: "Distinctive settings on the water, each with its own character." },
  { t: "Strong Communities", d: "A lasting commitment to the towns where we live and work." },
];

export default function Story() {
  return (
    <div className="pt-20">
      <PageHeader eyebrow="Our Story" title="Hospitality Runs in the Family."
        intro="From one waterfront restaurant on the Pagan River to a growing collection of destinations across Coastal Virginia." />

      <section className="mx-auto max-w-[1280px] px-5 pt-16 lg:px-8">
        <div className="overflow-hidden rounded-2xl">
          <img src="/assets/founders-dock.jpg" alt="The Pack brothers at the marina" className="aspect-[21/9] w-full object-cover" style={{ objectPosition: "center 40%" }} />
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-5 py-20 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="Family Roots" title="Growing Up Around the Station" />
            <p className="mt-5 text-[16px] leading-relaxed text-[rgba(48,56,60,0.8)]">
              For the Pack family, hospitality was never a business plan — it was a way of life. Growing up around Smithfield Station, the brothers learned that a great meal, a warm welcome, and a view of the water can turn an ordinary evening into a memory. That belief still guides everything we do.
            </p>
          </div>
          <img src="/assets/founders-sign.jpg" alt="The Pack brothers outside Smithfield Station" className="aspect-square w-full rounded-2xl object-cover shadow-[0_24px_60px_rgba(24,52,74,0.18)]" />
        </div>
      </section>

      <section className="bg-[#18344A] py-20 text-white">
        <div className="mx-auto max-w-[1000px] px-5 lg:px-8">
          <SectionHeading eyebrow="From One Destination to a Collection" title="A Growing Family of Places" light className="mb-14" />
          <div className="space-y-8">
            {TIMELINE.map((t) => (
              <div key={t.year} className="grid gap-3 border-l-2 border-[#B7A58E]/50 pl-6 sm:grid-cols-[120px_1fr]">
                <div className="font-serif text-2xl text-[#B7A58E]">{t.year}</div>
                <div>
                  <h3 className="font-serif text-xl text-white">{t.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-white/75">{t.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-5 py-20 lg:px-8">
        <SectionHeading eyebrow="Our Philosophy" title="What Guides Us" align="center" className="mb-12" />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PRINCIPLES.map((p) => (
            <div key={p.t} className="rounded-2xl border border-[var(--pb-border)] bg-white p-6">
              <h3 className="font-serif text-2xl text-[#18344A]">{p.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[rgba(48,56,60,0.72)]">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F5F3EF] py-20">
        <div className="mx-auto max-w-[900px] px-5 text-center lg:px-8">
          <SectionHeading eyebrow="Community" title="Proud of Where We're From" align="center"
            intro="From Smithfield to Suffolk, Surry to Fort Monroe, Pack Brothers Hospitality is woven into the fabric of Coastal Virginia — supporting local purveyors, hiring from our neighborhoods, and investing in the communities that welcomed us." />
          <Link to="/portfolio" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#18344A] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.06em] text-white hover:bg-[#18344A]/90">
            Explore Our Places <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
